import _ from 'lodash'
import { dayjsTz as dayjs } from '~/utils/dayjs'

// お知らせを「未読」に数える期間（公開日からの日数）。admin_announcements・Notion 共通。
// 過去分がずっと未読のままだとベルの数字が膨れて意味を失うので、直近だけを対象にする。
// - admin: 公開日（start_at → 無ければ created_at）がこれより前なら一覧には残し既読扱いにする。
//   日付が取れないものは今どおり未読。重要(important)の赤帯（unreadImportantAnnouncements）は対象外
// - Notion: admin と同じく、これより前の公開日のものは一覧には残し既読扱いにする
//   （2026-09-28: ベルが古い Notion を落としてダッシュボードのカードと中身が違ったため揃えた）
export const UNREAD_WINDOW_DAYS = 30

// 未読に数える期間の起点（この日の0時より前の公開日は未読に数えない）
const unreadWindowFrom = () =>
  dayjs().subtract(UNREAD_WINDOW_DAYS, 'd').startOf('day')

const toTime = value => {
  if (!value) return null
  const d = dayjs(value)
  return d.isValid() ? d.valueOf() : null
}

// 既読の持ち主（ログイン中のスタッフ）。uid が無ければ id。どちらも無ければ null（未ログイン）
export const staffReadKey = staff => {
  if (!staff) return null
  const key = staff.uid || staff.id
  return key ? String(key) : null
}

const uniqConcat = (base, ids) => {
  const next = (base || []).slice()
  ;(ids || []).forEach(id => {
    if (!next.includes(id)) next.push(id)
  })
  return next
}

const getDefaultState = () => {
  return {
    // 既読済みのお知らせID（スタッフごと）。{ [staff.uid]: [id, ...] }。LocalStorageで永続化。
    // ログアウトしても消さない（同じスタッフが再ログインしたら既読のまま）
    readIdsByStaff: {},
    // 旧形式（スタッフ別になる前）の既読ID。次にログインしたスタッフへ1回だけ引き継いで空にする
    readIds: [],
    // ログイン後のお知らせモーダル自動表示を判定済みか（utils/announcementAutoOpen）。
    // ログアウトの reset で false に戻るので、ログイン1回につき1回だけ判定する
    autoOpenDone: false
  }
}

export const state = () => getDefaultState()

export const mutations = {
  // ログアウト時（auth/logout）。既読（readIdsByStaff・旧 readIds）は残し、自動表示の判定だけ戻す
  reset(state) {
    state.autoOpenDone = false
  },

  setAutoOpenDone(state, value) {
    state.autoOpenDone = !!value
  },

  // 旧形式の readIds をこのスタッフの既読へ移す（1回だけ。移したら旧形式は空にする）
  claimLegacyReadIds(state, staffKey) {
    if (!staffKey || !state.readIds || state.readIds.length === 0) return
    const byStaff = state.readIdsByStaff || {}
    state.readIdsByStaff = {
      ...byStaff,
      [staffKey]: uniqConcat(byStaff[staffKey], state.readIds)
    }
    state.readIds = []
  },

  // { staffKey, id }
  markAsRead(state, { staffKey, id }) {
    mutations.markAllAsRead(state, { staffKey, ids: [id] })
  },

  // { staffKey, ids }
  markAllAsRead(state, { staffKey, ids }) {
    if (!staffKey) return
    const byStaff = state.readIdsByStaff || {}
    state.readIdsByStaff = {
      ...byStaff,
      [staffKey]: uniqConcat(byStaff[staffKey], ids)
    }
  }
}

export const getters = {
  // ログイン中スタッフの既読ID。赤帯・ベル・未読帯・モーダルの既読判定はすべてここを通る
  currentReadIds(state, getters, rootState) {
    const key = staffReadKey(rootState.auth && rootState.auth.staff)
    if (!key) return []
    return (state.readIdsByStaff && state.readIdsByStaff[key]) || []
  },

  // 有効なお知らせ一覧（期間内のもの）
  activeAnnouncements(state, getters, rootState) {
    const settings = rootState.auth.settings || []
    const announcementSetting = _.find(settings, ['label', 'admin_announcements'])
    if (!announcementSetting || !announcementSetting.data) return []

    const now = dayjs()
    const announcements = announcementSetting.data.filter(a => {
      const startAt = a.start_at ? dayjs(a.start_at) : null
      const endAt = a.end_at ? dayjs(a.end_at) : null

      // 開始日時チェック
      if (startAt && now.isBefore(startAt)) return false
      // 終了日時チェック
      if (endAt && now.isAfter(endAt)) return false

      return true
    })

    // priority順（降順）でソート
    return _.orderBy(announcements, ['priority'], ['desc'])
  },

  // 未読のお知らせ一覧
  unreadAnnouncements(state, getters) {
    return getters.activeAnnouncements.filter(
      a => !getters.currentReadIds.includes(a.id)
    )
  },


  // キャンペーンタイプのお知らせ
  campaignAnnouncements(state, getters) {
    return getters.activeAnnouncements.filter(a => a.type === 'campaign')
  },

  // 重要なお知らせ
  importantAnnouncements(state, getters) {
    return getters.activeAnnouncements.filter(a => a.type === 'important')
  },

  // 未読の重要告知（トップバナー用）。activeAnnouncements 由来で priority 降順。
  unreadImportantAnnouncements(state, getters) {
    return getters.unreadAnnouncements.filter(a => a.type === 'important')
  },

  // Notion のリリースノート（store/notion の news）。ページIDの無いもの・公開日の無いもの・
  // 公開日が未来のものは出さない。古いものも出す（既読扱いは announcementFeed で付ける）
  notionReleaseItems(state, getters, rootState) {
    const news = (rootState.notion && rootState.notion.news) || []
    const now = dayjs()
    const items = news
      .filter(n => {
        if (!n || !n.id || !n.public_date) return false
        const d = dayjs(n.public_date)
        if (!d.isValid()) return false
        return !d.isAfter(now)
      })
      .map(n => ({
        id: n.id,
        source: 'notion',
        type: 'release',
        title: n.title,
        body: null,
        link_url: n.public_url || null,
        banner_url: null,
        date: n.public_date
      }))
    return _.uniqBy(items, 'id')
  },

  // 運営からのお知らせの正本。ヘッダーのベル（AnnouncementModal・未読数バッジ）と
  // ダッシュボードのカード（NewsCard・先頭数件の抜粋）はどちらもこれを出す。
  // admin_announcements 全 type（期間内）＋ Notion。admin のリンク先と同じ公開URLの Notion は除く（重複）。
  // 未読が上・既読が下、それぞれ新しい順（同日時は admin の priority 順を保つ）。
  // 公開日が UNREAD_WINDOW_DAYS 日より前のものは既読扱い（read: true）で一覧に残す。
  announcementFeed(state, getters) {
    const fromTime = unreadWindowFrom().valueOf()
    // 重要（important）は赤帯と同じく期間（end_at）で運営が管理するので、30日で既読扱いにしない
    const isStale = a => {
      if (a.type === 'important') return false
      const t = toTime(a.date)
      return t !== null && t < fromTime
    }
    const adminItems = getters.activeAnnouncements.map(a => ({
      ...a,
      source: 'admin',
      type: a.type || 'default',
      date: a.start_at || a.created_at || null
    }))
    const adminLinks = adminItems.map(a => a.link_url).filter(Boolean)
    const notionItems = getters.notionReleaseItems.filter(
      n => !n.link_url || !adminLinks.includes(n.link_url)
    )
    const items = adminItems.concat(notionItems).map(a => ({
      ...a,
      read: getters.currentReadIds.includes(a.id) || isStale(a)
    }))
    return _.orderBy(
      items,
      [a => (a.read ? 1 : 0), a => toTime(a.date) || 0],
      ['asc', 'desc']
    )
  },

  // 未読（全種類）。announcementFeed の並びのまま
  unreadAll(state, getters) {
    return getters.announcementFeed.filter(a => !a.read)
  },

  // ベルのバッジに出す未読数（全種類）
  unreadCount(state, getters) {
    return getters.unreadAll.length
  },

  // 未読のうち最新1件（無ければ null）
  latestUnread(state, getters) {
    return getters.unreadAll[0] || null
  },

  // 重要(important)を除いた未読。重要は赤帯で出すので、ダッシュボードの
  // 「未読のお知らせ」帯はこちらを使う
  unreadNormal(state, getters) {
    return getters.unreadAll.filter(a => a.type !== 'important')
  }
}

export const actions = {
  // お知らせモーダルの自動表示を判定済みにし、開くべきか（重要を除く未読があるか）を返す。
  // 判定済みなら false。開いただけでは既読にしない（項目を開いたら既読・既存どおり）
  consumeAutoOpen(context) {
    if (context.state.autoOpenDone) return false
    context.commit('setAutoOpenDone', true)
    return (context.getters.unreadNormal || []).length > 0
  },

  // ログイン・アカウント切替のあとに呼ぶ（layouts/default.vue）。旧形式の既読を引き継ぐ
  syncStaff(context) {
    const key = staffReadKey(context.rootState.auth && context.rootState.auth.staff)
    if (key) context.commit('claimLegacyReadIds', key)
  },

  // 特定のお知らせを既読にする
  markAsRead(context, id) {
    const staffKey = staffReadKey(context.rootState.auth && context.rootState.auth.staff)
    if (!staffKey) return
    context.commit('claimLegacyReadIds', staffKey)
    context.commit('markAsRead', { staffKey, id })
  },

  // 一覧に出ている全てのお知らせ（admin ＋ Notion）を既読にする
  markAllAsRead(context) {
    const staffKey = staffReadKey(context.rootState.auth && context.rootState.auth.staff)
    if (!staffKey) return
    const ids = context.getters.announcementFeed.map(a => a.id)
    context.commit('claimLegacyReadIds', staffKey)
    context.commit('markAllAsRead', { staffKey, ids })
  }
}
